import { hasVersionHistory } from "@/domains/documents/utils/versionHistory";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useCallback, useMemo, useState, type ReactNode } from "react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { Loader2, AlertCircle } from "lucide-react";
import type {
  DocumentRowMapped,
  TimelineFilterParams,
  TimelineGranularity,
} from "@/stores/documents/repository";
import { zoomToGranularity } from "@/stores/documents/repository";
import { ZOOM_CONFIG } from "@/domains/documents/types/timeline";
import { useServerTimelineZoom } from "@/domains/documents/hooks/useServerTimelineZoom";
import { useDocumentViewer } from "@/domains/documents/hooks/useDocumentViewer";
import { useDocumentStore } from "@/hooks/documents/useDocumentStore";
import { useDocumentRename } from "@/domains/documents/hooks/useDocumentRename";
import { useDrawingViewer, isDwgParentDoc } from "@/domains/documents/hooks/useDrawingViewer";
import { usePermissions } from "@/hooks/usePermissions";
import { TimelineStem } from "../../molecules/Timeline/TimelineStem";
import { ServerTimelineCluster } from "../../molecules/Timeline/ServerTimelineCluster";
import { ServerTimelineDayBand } from "../../molecules/Timeline/ServerTimelineDayBand";
import {
  ServerTimelineNode,
  type TimelineSelectModifiers,
} from "../../molecules/Timeline/ServerTimelineNode";
import {
  useDocumentVerification,
  verifiedSelectionState,
} from "../../../hooks/useDocumentVerification";
import type { DocumentMenuConfig } from "../DocumentRowContextMenu";
import { useDocumentCommentsPanel } from "../../../comments/documentCommentsPanelContext";
import { useDocumentFiltersOptional } from "../../../context/DocumentFiltersContext";
import { DocumentsFilteredEmptyState } from "../../DocumentsFilteredEmptyState";
import { useDocumentCommentCountsQuery } from "@/stores/documents/comments/queries";
import { VersionDialogs } from "../VersionDialogs";
import { useAddNewVersionsDialog } from "@/domains/documents/hooks/useAddNewVersionsDialog";
import type { AddVersionsOutcome, StagedVersionItem } from "@/domains/documents/types/addVersions";
import { ConfirmDialog } from "../../molecules/ConfirmDialog";
import { TimelineGranularityControl } from "../../molecules/Timeline/TimelineGranularityControl";
import { TimelineDateBasisControl } from "../../molecules/Timeline/TimelineDateBasisControl";
import { useTimelineDateBasis } from "@/domains/documents/hooks/useTimelineDateBasis";
import { DocumentViewerDialog } from "../../DocumentViewerDialog";
import { TeachFranklinModal } from "../../TeachFranklinModal";
import { DocumentVersionHistory } from "../../DocumentVersionHistory";
import {
  bucketSlotCount,
  generateServerDateMarkers,
  TIMELINE_ENTRY_GAP,
} from "@/domains/documents/utils/timelineClustering";
import { getDocTimestamp, hasDocTimestamp } from "@/stores/documents/repository/documentDates";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { addCategoryToDocument, removeCategoryFromDocument } from "../../../utils/categoryHelpers";
import {
  addDisciplineToDocument,
  addStageToDocument,
  removeDisciplineFromDocument,
  removeStageFromDocument,
} from "../../../utils/tagHelpers";
import { ToastAction } from "@/components/ui/toast";
import { useAuthStore } from "@/hooks/useAuthStore";
import {
  resolveDocumentBreadcrumb,
  type FolderPathSegment,
} from "../../../utils/folderPathResolver";
import type { Document } from "@/types/documents";
import { getContentTypeFromPath } from "@/utils/previewUtils";
import { TIMELINE_DOCUMENT_CARD_FILL_CLASS } from "@/domains/documents/components/molecules/Timeline/timelineCard";

/**
 * Vertical slot every document takes in individual-docs mode — the same
 * constant gap cluster mode uses, so both modes read as one timeline.
 */
const DOC_NODE_HEIGHT = TIMELINE_ENTRY_GAP;

interface ServerDocumentTimelineProps {
  /** Building ID to load timeline for */
  buildingId: string;
  /** Additional CSS classes */
  className?: string;
  /** Filters to apply to timeline data (project, categories, disciplines, stages, search, folder) */
  filters?: TimelineFilterParams;
  /** Whether the total should be shown against the unfiltered scope count. */
  showFilteredTotal?: boolean;
  /** Fired once this tab's fetch for the current `filters` has settled. Backs
   *  the page-level search box's spinner — see useServerTimelineZoom. */
  onResolved?: () => void;
  /** Submit handler for inline document rename. When provided AND `canRenameDocument`
   *  returns true for a row, the timeline node's kebab menu shows a "Rename" item.
   *  The handler does the actual API call (with validation) and toast/refresh;
   *  the timeline owns the edit state via useDocumentRename. */
  onRenameDocumentSubmit?: (docId: string, newFilename: string) => Promise<void> | void;
  /** Predicate that gates the Rename action visibility per node. Hides when not provided. */
  canRenameDocument?: (doc: DocumentRowMapped) => boolean;
  /** Opens the share dialog for the current selection. Hides Share when absent. */
  onShare?: () => void;
  /** Sends the selected documents to the chat. Hides Ask Franklin when absent. */
  onAskFranklin?: (docs: Array<{ id: string; filename: string }>) => void;
  /** Commits the staged uploads / existing documents onto `doc` as
   *  consecutive new versions. Hides "Add new version" when absent. */
  onAddNewVersions?: (
    items: StagedVersionItem[],
    doc: {
      id: string;
      filename: string;
      version_group: string | null;
      project_id?: number | null;
      folder_id?: string | null;
      parent_document_id: string | null;
      container_type?: string | null;
    }
  ) => Promise<AddVersionsOutcome>;
}

/**
 * Resolve a Shift+click range selection on the timeline — the analogue of the
 * list table's `applyRangeSelection`. Returns the ids to select and the anchor
 * to keep. When the anchor is still a live, selected row the range REPLACES
 * this surface's selection (the caller deselects on-screen rows outside
 * [lo, hi]; off-screen/other-surface selections stay); otherwise it collapses
 * to just the clicked row. Extracted so `handleDocumentSelect` stays simple.
 */
function resolveTimelineRange(
  docId: string,
  order: string[],
  anchor: string | null,
  isSelected: (id: string) => boolean
): { ids: string[]; anchor: string } {
  const anchorIdx = anchor === null ? -1 : order.indexOf(anchor);
  const targetIdx = order.indexOf(docId);
  if (anchor !== null && anchorIdx !== -1 && targetIdx !== -1 && isSelected(anchor)) {
    const [lo, hi] = anchorIdx <= targetIdx ? [anchorIdx, targetIdx] : [targetIdx, anchorIdx];
    return { ids: order.slice(lo, hi + 1), anchor };
  }
  return { ids: [docId], anchor: docId };
}

/**
 * ServerDocumentTimeline - Server-side aggregated timeline
 *
 * Uses server-side aggregation for efficient handling of 6000+ documents.
 * Only fetches bucketed counts on initial load, then lazy loads documents
 * when clusters are expanded.
 *
 * At max zoom (90+), switches to individual documents mode where each document
 * is rendered separately with infinite scroll pagination.
 *
 * Key features:
 * - Fetches aggregated buckets from database (not full document list)
 * - Documents are lazy-loaded on cluster expansion
 * - Supports pagination within expanded clusters
 * - Max zoom mode renders individual documents with scroll loading
 */
export const ServerDocumentTimeline = observer(function ServerDocumentTimeline({
  buildingId,
  className,
  filters,
  showFilteredTotal = false,
  onResolved,
  onRenameDocumentSubmit,
  canRenameDocument,
  onShare,
  onAskFranklin,
  onAddNewVersions,
}: ServerDocumentTimelineProps) {
  const { t } = useLanguage();
  const docFilters = useDocumentFiltersOptional();
  // "Date: Upload / Creation". The basis rides with the other filters, so a
  // switch clears and reloads the timeline exactly as a filter change does.
  // Creation (`file`) is each RPC's default and is not sent at all.
  const {
    basis: dateBasis,
    setBasis: setDateBasis,
    uploadUnavailable: dateBasisUploadUnavailable,
  } = useTimelineDateBasis();
  const timelineFilters = useMemo<TimelineFilterParams | undefined>(
    () => (dateBasis === "upload" ? { ...filters, dateBasis } : filters),
    [filters, dateBasis]
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const wheelHandlerRef = useRef<((e: WheelEvent) => void) | null>(null);

  // Container height for timeline calculations
  const containerHeight = 800;
  // Breathing room above the first entry and below the last one. Deliberately
  // modest: a taller pad left the granularity row stranded in whitespace.
  const TIMELINE_PADDING = 40;
  const usableHeight = containerHeight - TIMELINE_PADDING * 2;

  // Document viewer hook - handles blob loading and dialog state
  const viewer = useDocumentViewer();

  // Document store for selection and download
  const documentStore = useDocumentStore();
  const { toast } = useToast();
  const authStore = useAuthStore();
  const { canViewDrawingCompareTool } = usePermissions();
  // Per-row "View drawing" only — the timeline's multi-select actions live in
  // its bulk bar (DocumentsMainView), so no candidate/selection list is needed.
  const { canUseDrawingViewer, openDrawingViewer } = useDrawingViewer([], undefined, {
    enabled: canViewDrawingCompareTool,
    buildingId,
  });

  // Track which cluster's inline list is open (only one at a time)
  const [openClusterId, setOpenClusterId] = useState<string | null>(null);

  // Inline document rename — owned by the timeline so the same edit-in-progress
  // state holds whether the node is rendered as a top-level node, inside a
  // single-doc cluster slot, or inside a cluster's inline expansion popover.
  const { canVerifyDocument, setVerified } = useDocumentVerification();
  const commentsPanel = useDocumentCommentsPanel();
  const commentCounts = useDocumentCommentCountsQuery(
    Number(buildingId),
    documentStore.timelineVisibleDocumentIds,
    Boolean(commentsPanel)
  );
  const openComments = useCallback(
    (doc: { id: string | number; filename: string }) => {
      commentsPanel?.open({
        documentId: Number(doc.id),
        buildingId: Number(buildingId),
        filename: doc.filename,
      });
    },
    [commentsPanel, buildingId]
  );

  const documentRename = useDocumentRename(async (id, newFilename) => {
    if (!onRenameDocumentSubmit) return;
    await onRenameDocumentSubmit(String(id), newFilename);
  });

  // Per-row menu props. Rename and Verify share one builder so every node
  // rendering — top-level, single-doc cluster slot, cluster expansion popover —
  // gets the same menu without threading two callbacks through three levels.
  const buildRenameProps = useCallback(
    (doc: DocumentRowMapped) => ({
      onRename:
        onRenameDocumentSubmit && (canRenameDocument?.(doc) ?? false)
          ? () => documentRename.startRename({ id: doc.id, filename: doc.filename })
          : undefined,
      onToggleVerified: canVerifyDocument(doc)
        ? () => void setVerified([doc], doc.verified_at == null)
        : undefined,
      isRenaming: documentRename.isEditing(doc.id),
      renameBasename: documentRename.editBasename,
      renameExtension: documentRename.extension,
      onRenameChange: documentRename.setEditBasename,
      onRenameConfirm: () => {
        void documentRename.confirmRename();
      },
      onRenameCancel: documentRename.cancelRename,
    }),
    [canRenameDocument, canVerifyDocument, documentRename, onRenameDocumentSubmit, setVerified]
  );

  // Teach Franklin modal state
  const [teachFranklinDoc, setTeachFranklinDoc] = useState<Document | null>(null);
  const [teachFranklinOpen, setTeachFranklinOpen] = useState(false);

  // Version history modal state (opened from the timeline's version badge)
  const [versionHistoryState, setVersionHistoryState] = useState<{
    open: boolean;
    versionGroup: string | null;
    filename: string;
  }>({ open: false, versionGroup: null, filename: "" });

  const handleShowVersionHistory = useCallback((doc: DocumentRowMapped) => {
    if (!doc.version_group) return;
    setVersionHistoryState({
      open: true,
      versionGroup: doc.version_group,
      filename: doc.filename,
    });
  }, []);

  const handlePreviewVersion = useCallback(
    (documentId: string, filePath: string, versionFilename?: string) => {
      // Route the viewer to the right renderer: content_type from the doc
      // row isn't propagated through the DocumentVersionHistory modal's
      // onPreview contract, so derive it from the storage path's extension.
      // Use the version's own filename when provided so the viewer header
      // shows the user-facing name; fall back to the anchor doc's name.
      const contentType = getContentTypeFromPath(filePath);
      viewer.openViewer({
        id: documentId,
        filename: versionFilename?.trim() || versionHistoryState.filename,
        file_path: filePath,
        building_id: buildingId,
        content_type: contentType,
        size: 0,
        created_at: new Date().toISOString(),
        parent_document_id: null,
      });
    },
    [viewer, versionHistoryState.filename, buildingId]
  );

  const handleToggleInlineCluster = useCallback((clusterId: string) => {
    setOpenClusterId((prev) => (prev === clusterId ? null : clusterId));
  }, []);

  // Server-side timeline hook
  const {
    zoomLevel,
    setZoomLevel,
    timelineData,
    dateMarkers,
    handleWheelZoom,
    isLoading,
    error,
    totalDocuments,
    granularity,
    // Cluster expansion
    expandCluster,
    collapseCluster,
    loadMoreInCluster,
    getExpandedCluster,
    isClusterExpanded,
    // Individual docs mode
    isMaxZoomMode,
    individualDocs,
    hasMoreIndividualDocs,
    individualDocsTruncated,
    isLoadingIndividualDocs,
    loadMoreIndividualDocs,
  } = useServerTimelineZoom({
    buildingId,
    containerHeight: usableHeight,
    initialZoomLevel: ZOOM_CONFIG.DEFAULT,
    filters: timelineFilters,
    onResolved,
  });

  // Keep wheel handler ref in sync
  useEffect(() => {
    wheelHandlerRef.current = handleWheelZoom;
  }, [handleWheelZoom]);

  // Set up scroll wheel zoom handler
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const wheelHandler = (e: WheelEvent) => {
      wheelHandlerRef.current?.(e);
    };

    container.addEventListener("wheel", wheelHandler, { passive: false });
    return () => container.removeEventListener("wheel", wheelHandler);
  }, []);

  // Fetch the documents of every bucket that renders document cards (single-doc
  // buckets at any zoom, small buckets at day zoom). Max zoom mode gets its
  // documents from a separate fetch, not from cluster expansion.
  useEffect(() => {
    if (isMaxZoomMode) return;

    for (const cluster of timelineData.clusters) {
      if (cluster.showsDocumentCards && !isClusterExpanded(cluster)) {
        expandCluster(cluster);
      }
    }
  }, [timelineData.clusters, isClusterExpanded, expandCluster, isMaxZoomMode]);

  // Handle scroll for infinite loading in max zoom mode
  const handleScroll = useCallback(() => {
    if (!isMaxZoomMode || !hasMoreIndividualDocs || isLoadingIndividualDocs) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    // Load more when user scrolls near the bottom (within 200px)
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      loadMoreIndividualDocs();
    }
  }, [isMaxZoomMode, hasMoreIndividualDocs, isLoadingIndividualDocs, loadMoreIndividualDocs]);

  // Add scroll listener for infinite loading
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !isMaxZoomMode) return;

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [isMaxZoomMode, handleScroll]);

  // Handle document view - opens viewer via hook
  const handleDocumentView = useCallback(
    (document: DocumentRowMapped) => {
      viewer.openViewer({
        id: document.id,
        filename: document.filename,
        file_path: document.file_path,
        building_id: document.building_id,
        content_type: document.content_type,
        size: document.size,
        created_at: document.created_at,
        parent_document_id: document.parent_document_id,
        version: document.version,
        created_by_name: document.created_by_name,
      });
    },
    [viewer]
  );

  // Flat, render-ordered ids of every individually-selectable row on screen
  // (max-zoom individual docs, or the docs inside expanded clusters — a
  // single-doc cluster renders as one node too). Shift-click range selection
  // resolves against this: the timeline analogue of the list table's
  // `unifiedItems`. Computed each render (not memoized) so it stays in sync
  // when a cluster expands mid-session.
  const orderedRowIds: string[] = isMaxZoomMode
    ? individualDocs.map((d) => String(d.id))
    : timelineData.clusters.flatMap((cluster) => {
        // Only rows actually on screen: card-mode buckets plus the one open
        // popover. expandedClusters also caches every popover ever opened —
        // including those would let a Shift+click range sweep up invisible rows.
        if (!cluster.showsDocumentCards && cluster.id !== openClusterId) return [];
        const expanded = getExpandedCluster(cluster);
        return expanded?.documents?.map((doc) => String(doc.id)) ?? [];
      });
  // Held in refs so the selection handler stays referentially stable —
  // re-creating it whenever the ordered list changes would re-render every node.
  const orderedRowIdsRef = useRef<string[]>(orderedRowIds);
  orderedRowIdsRef.current = orderedRowIds;
  // Anchor row for Shift+click range selection (like the list table's anchor).
  const selectionAnchorRef = useRef<string | null>(null);

  // Handle document selection — parity with the documents list table:
  //  • plain click / checkbox / Cmd·Ctrl → toggle this row, keep the rest
  //    selected (the old code cleared the selection on every plain click, so a
  //    second click deselected the first — timeline multi-select never worked)
  //  • Shift+click → select the range from the anchor row to this one
  const handleDocumentSelect = useCallback(
    (document: DocumentRowMapped, selected: boolean, modifiers: TimelineSelectModifiers) => {
      const docId = String(document.id);

      if (modifiers.rangeSelect) {
        // Successive Shift+clicks extend from the same anchor (resolveTimelineRange
        // returns the current anchor unchanged when the range is live).
        const { ids, anchor } = resolveTimelineRange(
          docId,
          orderedRowIdsRef.current,
          selectionAnchorRef.current,
          (id) => documentStore.isSelected(id)
        );
        // Deselect only within this surface's rendered rows — the analogue of
        // applyRangeSelection iterating its own unifiedItems. A global
        // clearSelection() here would drop selections held by closed-popover
        // rows or carried over from the list view.
        const inRange = new Set(ids);
        orderedRowIdsRef.current.forEach((id) => {
          if (!inRange.has(id)) documentStore.selectDocument(id, false);
        });
        ids.forEach((id) => documentStore.selectDocument(id, true));
        selectionAnchorRef.current = anchor;
        return;
      }

      documentStore.selectDocument(docId, selected);
      selectionAnchorRef.current = docId;
    },
    [documentStore]
  );

  // Handle document download
  const handleDocumentDownload = useCallback(
    (document: DocumentRowMapped) => {
      documentStore.downloadDocument(String(document.id));
    },
    [documentStore]
  );

  // Handle Teach Franklin - convert DocumentRowMapped to Document type for modal
  const handleTeachFranklin = useCallback((doc: DocumentRowMapped) => {
    const documentForModal: Document = {
      id: String(doc.id),
      filename: doc.filename,
      file_path: doc.file_path,
      building_id: String(doc.building_id),
      content_type: doc.content_type ?? "",
      size: doc.size ?? 0,
      created_at: doc.created_at,
      parent_document_id: doc.parent_document_id ? String(doc.parent_document_id) : null,
      version: doc.version ?? undefined,
      created_by_name: doc.created_by_name ?? undefined,
      state: doc.state as Document["state"],
      categories: doc.categories?.map((c) => ({
        id: c.id,
        name: c.name,
        name_et: c.name_et,
      })),
      design_disciplines: doc.design_disciplines?.map((d) => ({
        id: d.id,
        name: d.name,
        name_en: d.name_en,
        abbreviation: d.abbreviation,
        description: d.description,
      })),
      design_stages: doc.design_stages?.map((s) => ({
        id: s.id,
        name: s.name,
        name_en: s.name_en,
        abbreviation: s.abbreviation,
      })),
    };
    setTeachFranklinDoc(documentForModal);
    setTeachFranklinOpen(true);
  }, []);

  // Handle document update from Teach Franklin modal
  const handleDocumentUpdate = useCallback(
    (updatedDoc: Document) => {
      if (documentStore && updatedDoc.id) {
        const docId = parseInt(updatedDoc.id, 10);
        if (!isNaN(docId)) {
          setTimeout(() => {
            documentStore.refreshSingleDocument(docId);
          }, 500);
        }
      }
    },
    [documentStore]
  );

  // Handle tag removal from timeline cards
  const handleTagRemove = useCallback(
    async (
      doc: DocumentRowMapped,
      tagType: "category" | "discipline" | "stage",
      tagId: number,
      tagName: string
    ) => {
      const numericDocId = doc.id;
      try {
        if (tagType === "category") {
          await removeCategoryFromDocument(numericDocId, tagName);
        } else if (tagType === "discipline") {
          await removeDisciplineFromDocument(numericDocId, tagId);
        } else if (tagType === "stage") {
          await removeStageFromDocument(numericDocId, tagId);
        }

        const refreshDoc = () => {
          if (documentStore) {
            setTimeout(() => documentStore.refreshSingleDocument(numericDocId), 300);
            if (buildingId) {
              setTimeout(() => documentStore.loadAvailableFilters(buildingId), 500);
            }
          }
        };

        refreshDoc();

        toast({
          description: `Tag "${tagName}" removed`,
          action: (
            <ToastAction
              altText="Undo tag removal"
              onClick={async () => {
                try {
                  const userId = authStore.userProfileId;
                  if (!userId) throw new Error("Not authenticated");
                  if (tagType === "category") {
                    await addCategoryToDocument(numericDocId, tagName, userId);
                  } else if (tagType === "discipline") {
                    await addDisciplineToDocument(numericDocId, tagId, userId);
                  } else if (tagType === "stage") {
                    await addStageToDocument(numericDocId, tagId, userId);
                  }
                  refreshDoc();
                  toast({ description: `Tag "${tagName}" restored` });
                } catch {
                  toast({
                    variant: "destructive",
                    description: `Failed to restore tag "${tagName}"`,
                  });
                }
              }}
            >
              Undo
            </ToastAction>
          ),
        });
      } catch (error) {
        console.error("Failed to remove tag:", error);
        toast({ variant: "destructive", description: `Failed to remove tag "${tagName}"` });
      }
    },
    [documentStore, buildingId, toast]
  );

  // Resolve breadcrumb segments for a document using the store's project/folder maps
  const getDocumentBreadcrumb = useCallback(
    (doc: DocumentRowMapped): FolderPathSegment[] | null => {
      return resolveDocumentBreadcrumb(
        { project_id: doc.project_id, folder_id: doc.folder_id, filename: doc.filename },
        documentStore.tags.projects as Map<string, { name: string }>,
        documentStore.tags.folders as Map<
          string,
          { folder_path?: string | null; name?: string; project_id?: number | null }
        >
      );
    },
    [documentStore.tags.projects, documentStore.tags.folders]
  );

  // Check if document is selected
  const isDocumentSelected = useCallback(
    (documentId: string) => {
      return documentStore.isSelected(documentId);
    },
    [documentStore]
  );

  // "Add new version" — the dialog owns staging and ordering; the timeline
  // only owns which document is targeted (same split as the list).
  const handleAddNewVersions = useCallback(
    (items: StagedVersionItem[], doc: DocumentRowMapped) => {
      if (!onAddNewVersions) return Promise.resolve({ committed: 0, touchedVersionGroups: [] });
      return onAddNewVersions(items, {
        id: String(doc.id),
        filename: doc.filename,
        version_group: doc.version_group,
        project_id: doc.project_id ?? null,
        folder_id: doc.folder_id != null ? String(doc.folder_id) : null,
        parent_document_id: doc.parent_document_id != null ? String(doc.parent_document_id) : null,
        container_type: doc.container_type ?? null,
      });
    },
    [onAddNewVersions]
  );
  const addVersions = useAddNewVersionsDialog(
    onAddNewVersions ? handleAddNewVersions : undefined,
    undefined,
    "number"
  );
  // Single-row delete needs its own confirmation; the context menu only
  // confirms multi-delete internally.
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; filename: string } | null>(null);

  /**
   * Right-click menu for a timeline card — the same action set as the list's,
   * so the two tabs behave identically.
   *
   * Selection-based actions (Share, Ask Franklin, Delete) read the store, so a
   * right-click on an unselected row seeds the selection with that row first —
   * `ensureSingle`, mirroring the list. An existing selection is never cleared;
   * when exactly one row is checked, it wins over the hovered row.
   *
   * Move/Copy and Compare stay out: the timeline hosts neither dialog, and a
   * menu item with no handler is a dead item.
   */
  const buildContextMenu = useCallback(
    (clicked: DocumentRowMapped): DocumentMenuConfig => {
      const selected = documentStore.selectedVerifiableDocuments;
      const isMulti = selected.length > 1;
      // The checkbox selection wins over the hovered row (same rule as
      // DocumentListTable's buildDocMenuConfig): with exactly one row checked
      // and a different row right-clicked, every single-mode action targets
      // the checked row — otherwise Share (which reads the store selection)
      // and the rest of the menu would split across two documents.
      const doc =
        selected.length === 1 && !documentStore.isSelected(String(clicked.id))
          ? (documentStore.timelineRowById(selected[0].id) ?? clicked)
          : clicked;
      const ensureSingle = () => {
        if (isMulti || selected.length === 1) return;
        documentStore.selectDocument(String(doc.id), true);
      };
      const targets = isMulti
        ? selected
        : [
            {
              id: String(doc.id),
              project_id: doc.project_id ?? null,
              verified_at: doc.verified_at ?? null,
            },
          ];
      // All-or-nothing, like the list: a partially-permitted bulk write would
      // half-succeed and leave the rest to be rejected by RLS.
      const canVerify = targets.length > 0 && targets.every((d) => canVerifyDocument(d));
      const state = verifiedSelectionState(targets);

      return {
        isMulti,
        selectedCount: isMulti ? selected.length : 1,
        filename: isMulti ? undefined : doc.filename,
        isDwg: !!doc.filename?.toLowerCase().endsWith(".dwg"),
        canCompare: false,
        onShare: onShare
          ? () => {
              ensureSingle();
              onShare();
            }
          : undefined,
        onDownload: () => {
          if (isMulti) {
            // Download in multi mode means the whole selection (same rule as
            // the list's buildDocMenuConfig). Timeline rows never land in the
            // store's `documents` map, so the no-arg bulkDownload — which
            // resolves the selection there — would drop them; resolve each
            // selected id from the timeline's own row lists instead.
            const rows = selected
              .map((s) => documentStore.timelineRowById(s.id))
              .filter((row): row is DocumentRowMapped => !!row)
              .map((row) => ({
                id: String(row.id),
                filename: row.filename,
                file_path: row.file_path,
              }));
            documentStore.bulkDownload(rows).catch((error) => {
              console.error("Bulk download failed:", error);
            });
          } else {
            handleDocumentDownload(doc);
          }
        },
        onAddNewVersion: !isMulti && onAddNewVersions ? () => addVersions.openFor(doc) : undefined,
        opensVersioning: hasVersionHistory(doc),
        onAskFranklin: onAskFranklin
          ? () => {
              ensureSingle();
              // selectedVerifiableDocuments resolves filenames from the
              // timeline's own row lists — the `documents` map is empty here.
              const rows = isMulti ? selected : [doc];
              onAskFranklin(rows.map((r) => ({ id: String(r.id), filename: r.filename })));
            }
          : undefined,
        onDelete: () => {
          if (isMulti) {
            documentStore.bulkMoveToTrash().catch((error) => {
              console.error("Bulk move to trash failed:", error);
            });
          } else {
            setDeleteConfirm({ id: String(doc.id), filename: doc.filename });
          }
        },
        onRename:
          !isMulti && onRenameDocumentSubmit && (canRenameDocument?.(doc) ?? false)
            ? () => documentRename.startRename({ id: doc.id, filename: doc.filename })
            : undefined,
        verifiedState: state,
        onToggleVerified: canVerify ? () => void setVerified(targets, state !== "all") : undefined,
        onComments: !isMulti && commentsPanel ? () => openComments(doc) : undefined,
      };
    },
    [
      canRenameDocument,
      canVerifyDocument,
      commentsPanel,
      openComments,
      documentRename,
      documentStore,
      handleDocumentDownload,
      onAskFranklin,
      onRenameDocumentSubmit,
      onShare,
      onAddNewVersions,
      setVerified,
    ]
  );

  // Everything a document card needs, wherever it is rendered — a Days-view
  // stack, a cluster slot or the max-zoom list. The three paths differ only in
  // where the card sits, never in what it can do.
  const documentNodeProps = useCallback(
    (doc: DocumentRowMapped) => ({
      document: doc,
      isSelected: isDocumentSelected(String(doc.id)),
      onView: handleDocumentView,
      onSelect: handleDocumentSelect,
      onDownload: handleDocumentDownload,
      onTeachFranklin: handleTeachFranklin,
      onTagRemove: handleTagRemove,
      onShowVersionHistory: handleShowVersionHistory,
      // Parity with the list view's row menu — a parent DWG opens in the
      // Drawings tool straight from its timeline card.
      onViewDrawing:
        canUseDrawingViewer && isDwgParentDoc(doc) ? () => openDrawingViewer([doc.id]) : undefined,
      breadcrumb: getDocumentBreadcrumb(doc),
      buildingId,
      isContentMatch: documentStore.isTimelineContentMatch(String(doc.id)),
      contentMatchInfo: documentStore.getTimelineContentMatchInfo(String(doc.id)),
      contextMenu: buildContextMenu(doc),
      onComments: commentsPanel ? openComments : undefined,
      commentCounts: commentCounts.get(Number(doc.id)),
      dateBasis,
      ...buildRenameProps(doc),
    }),
    [
      dateBasis,
      buildContextMenu,
      buildRenameProps,
      buildingId,
      commentsPanel,
      commentCounts,
      openComments,
      canUseDrawingViewer,
      documentStore,
      getDocumentBreadcrumb,
      handleDocumentDownload,
      handleDocumentSelect,
      handleDocumentView,
      handleShowVersionHistory,
      handleTagRemove,
      handleTeachFranklin,
      isDocumentSelected,
      openDrawingViewer,
    ]
  );

  // Calculate content height and date markers for individual docs mode
  const individualDocsLayout = useMemo(() => {
    if (!isMaxZoomMode || individualDocs.length === 0) {
      return { contentHeight: containerHeight, markers: [] };
    }

    // Calculate content height based on number of docs
    // Each doc takes DOC_NODE_HEIGHT pixels of vertical space
    const contentHeight = Math.max(
      individualDocs.length * DOC_NODE_HEIGHT + TIMELINE_PADDING * 2,
      containerHeight
    );

    // Generate date markers based on document timestamps
    const docs = individualDocs;
    if (docs.length === 0) return { contentHeight, markers: [] };

    // The span picks the marker FORMAT, so a dateless row placed at the epoch
    // would turn every label into a bare year. It is not a date, so it does not
    // get a vote on the range — the rows that have one bound it.
    const dated = docs.filter((doc) => hasDocTimestamp(doc, dateBasis));
    // Nothing to bound the range with: every row would answer the epoch, so the
    // rail would read "12:00 AM" over and over against dates in 1970. The
    // document cards still render — they just get no date markers beside them.
    if (dated.length === 0) return { contentHeight, markers: [] };
    const start = getDocTimestamp(dated[dated.length - 1], dateBasis);
    const end = getDocTimestamp(dated[0], dateBasis);
    const timeRange = { start, end, durationMs: end.getTime() - start.getTime() };

    // Create pseudo-clusters for date marker generation. Placed by the same date
    // the card prints and the server ordered the page by — see getDocTimestamp.
    const pseudoClusters = docs.map((doc, index) => ({
      id: `doc-${doc.id}`,
      startTime: getDocTimestamp(doc, dateBasis),
      endTime: getDocTimestamp(doc, dateBasis),
      documentCount: 1,
      side: (index % 2 === 0 ? "left" : "right") as "left" | "right",
      pixelPosition: index * DOC_NODE_HEIGHT,
      normalizedPosition: 0,
    }));

    const markers = generateServerDateMarkers(pseudoClusters, timeRange, zoomLevel);

    return { contentHeight, markers };
  }, [isMaxZoomMode, individualDocs, containerHeight, zoomLevel, dateBasis]);

  // Toolbar row: the document count and the days/weeks/months/years buttons sit
  // together at the right end, count first and the buttons next to it.
  const toolbarRow = (count: ReactNode) => (
    <div className="flex flex-wrap items-center justify-end gap-3">
      <div className="text-sm text-muted-foreground">{count}</div>
      <TimelineDateBasisControl
        value={dateBasis}
        onValueChange={setDateBasis}
        uploadUnavailable={dateBasisUploadUnavailable}
      />
      <TimelineGranularityControl zoomLevel={zoomLevel} onZoomChange={setZoomLevel} />
    </div>
  );

  /**
   * Every dialog the timeline can open, in one place. Both render paths — the
   * max-zoom individual list and the cluster view — render this same element,
   * so an addition here reaches both instead of drifting between two copies.
   */
  const timelineDialogs = (
    <>
      <DocumentViewerDialog
        document={viewer.document}
        blobUrl={viewer.blobUrl}
        blobData={viewer.blobData}
        open={viewer.isOpen}
        onOpenChange={viewer.setIsOpen}
        originalDwgFilePath={viewer.originalDwgFilePath ?? undefined}
      />

      {/* Teach Franklin modal for tag correction */}
      {teachFranklinDoc && (
        <TeachFranklinModal
          open={teachFranklinOpen}
          onOpenChange={(open) => {
            setTeachFranklinOpen(open);
            if (!open) {
              setTeachFranklinDoc(null);
            }
          }}
          document={teachFranklinDoc}
          onDocumentUpdate={handleDocumentUpdate}
        />
      )}

      {/* Version history modal — opened from the timeline node's version badge */}
      <DocumentVersionHistory
        open={versionHistoryState.open}
        onOpenChange={(open) => {
          if (!open) setVersionHistoryState({ open: false, versionGroup: null, filename: "" });
        }}
        versionGroup={versionHistoryState.versionGroup}
        filename={versionHistoryState.filename}
        onPreview={handlePreviewVersion}
      />

      {/* Versioning / Add new version — the same dialogs the list uses, so
          staging and ordering behave identically on both tabs. */}
      <VersionDialogs
        state={addVersions}
        buildingId={buildingId}
        onPreviewVersion={handlePreviewVersion}
        target={
          addVersions.target
            ? {
                id: String(addVersions.target.id),
                filename: addVersions.target.filename,
                version: addVersions.target.version,
                versionGroup: addVersions.target.version_group,
                projectId: addVersions.target.project_id ?? null,
                parentDocumentId:
                  addVersions.target.parent_document_id != null
                    ? String(addVersions.target.parent_document_id)
                    : null,
              }
            : null
        }
      />

      {/* Single-row delete confirmation. The context menu confirms multi-delete
          itself, so this only covers the one-row case. */}
      <ConfirmDialog
        open={deleteConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirm(null);
        }}
        title={t.documents.actions.deleteDocumentTitle}
        description={
          deleteConfirm
            ? t.documents.actions.deleteDocumentConfirm.replace(
                "{filename}",
                deleteConfirm.filename
              )
            : t.documents.actions.deleteDocumentConfirmFallback
        }
        confirmLabel={t.common.delete}
        cancelLabel={t.common.cancel}
        variant="destructive"
        onConfirm={() => {
          const target = deleteConfirm;
          setDeleteConfirm(null);
          if (!target) return;
          documentStore.deleteDocument(target.id).catch((error) => {
            console.error("Delete document failed:", error);
          });
        }}
      />
    </>
  );

  const isDayView = zoomToGranularity(zoomLevel) === "day";
  // `null` means the building-wide count has not been counted (or its query
  // failed) — show the filtered total alone rather than a made-up denominator.
  const buildingDocumentCount = documentStore.buildingDocumentCount;
  // The two numbers come from different queries: the numerator from the
  // timeline RPC, the denominator from `fetchDocumentCount`. They apply the same
  // row rules (hidden, dwg-child and enclosed-signed-container rows excluded;
  // the RPC's hidden filter is franklin-data-pipeline migration
  // 20260922113938), but they are separate reads and can still disagree — a
  // realtime refresh landing between them, or a rule that drifts later — which
  // would read as "1,240 of 1,200". A denominator smaller than the numerator is known
  // to be wrong, so drop that half rather than clamp — clamping would keep
  // rendering a ratio built from a number we know does not match.
  const usableTotal =
    buildingDocumentCount !== null && totalDocuments <= buildingDocumentCount
      ? buildingDocumentCount
      : null;
  // Two strings, not a ratio pushed through one: `documentCount` is written
  // for a single count in both locales.
  const totalCountLabel =
    showFilteredTotal && usableTotal !== null
      ? t.timeline.documentsOfTotal
          .replace("{loaded}", totalDocuments.toLocaleString())
          .replace("{total}", usableTotal.toLocaleString())
      : t.timeline.documentCount.replace("{count}", totalDocuments.toLocaleString());

  // Error state
  if (error) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-lg border border-destructive/30 bg-destructive/5 p-12",
          className
        )}
      >
        <div className="flex flex-col items-center gap-3">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="text-sm font-medium text-destructive">{t.timeline.failedLoad}</p>
          <p className="text-xs text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading && timelineData.clusters.length === 0 && individualDocs.length === 0) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-lg border bg-muted/10 p-12",
          className
        )}
      >
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t.timeline.loading}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (totalDocuments === 0) {
    const hasActiveSearch = !!filters?.searchText?.trim();

    // When filters or a search hide every document, say so and keep the
    // toolbar visible, rather than the "nothing uploaded yet" card below.
    if (showFilteredTotal || hasActiveSearch) {
      return (
        <div className={cn("flex flex-col gap-2", className)}>
          {toolbarRow(totalCountLabel)}
          <DocumentsFilteredEmptyState
            className="rounded-lg border bg-muted/10"
            onClearFilters={docFilters?.clearFilters}
          />
        </div>
      );
    }

    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-lg border bg-muted/10 p-12",
          className
        )}
      >
        <div className="flex flex-col items-center gap-2">
          <p className="text-sm font-medium text-muted-foreground">{t.timeline.noDocuments}</p>
          <p className="text-xs text-muted-foreground">{t.timeline.uploadPrompt}</p>
        </div>
      </div>
    );
  }

  // Render individual docs mode (max zoom)
  if (isMaxZoomMode) {
    const { contentHeight, markers } = individualDocsLayout;

    return (
      <div className={cn("flex flex-col gap-2", className)} ref={containerRef}>
        {toolbarRow(
          <>
            {totalCountLabel}
            <span className="ml-1">
              {t.timeline.showingCount.replace(
                "{count}",
                `${individualDocs.length}${hasMoreIndividualDocs ? "+" : ""}`
              )}
            </span>
          </>
        )}

        {/* Timeline container */}
        <div
          ref={scrollContainerRef}
          // No panel frame — the timeline sits on the plain white page canvas,
          // matching the other document views.
          className="relative overflow-auto"
          style={{ height: containerHeight }}
        >
          <div
            className="relative px-6"
            style={{
              width: "100%",
              minHeight: contentHeight,
              paddingTop: TIMELINE_PADDING,
              paddingBottom: TIMELINE_PADDING,
            }}
          >
            {/* Central stem */}
            <div
              className="pointer-events-none absolute left-1/2 -translate-x-1/2"
              style={{
                top: TIMELINE_PADDING / 2,
                height: contentHeight - TIMELINE_PADDING,
              }}
            >
              <TimelineStem height={contentHeight - TIMELINE_PADDING} dateMarkers={markers} />
            </div>

            {/* Render individual documents */}
            {individualDocs.map((doc, index) => {
              const pixelPosition = index * DOC_NODE_HEIGHT + TIMELINE_PADDING;
              const side = index % 2 === 0 ? "left" : "right";

              return (
                <ServerTimelineNode
                  key={doc.id}
                  side={side}
                  pixelPosition={pixelPosition}
                  {...documentNodeProps(doc)}
                />
              );
            })}

            {/* Load more indicator. `truncated` is a list whose paging gave
                up short of the end, not one that reached it — the rest is
                still there, so offer the re-read rather than ending here. It
                is a button and not the scroll handler above, so a re-read that
                keeps dead-ending cannot refetch on every scroll event. */}
            {(hasMoreIndividualDocs || individualDocsTruncated) && (
              <div
                className="absolute left-1/2 flex -translate-x-1/2 justify-center"
                style={{ top: individualDocs.length * DOC_NODE_HEIGHT + TIMELINE_PADDING + 20 }}
              >
                {isLoadingIndividualDocs ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">{t.timeline.loadingMore}</span>
                  </div>
                ) : (
                  <Button variant="ghost" size="sm" onClick={loadMoreIndividualDocs}>
                    {t.timeline.loadMore}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Zoom hint */}
        <p className="text-center text-xs text-muted-foreground">
          {t.timeline.hintZoomScrollInfinite}
        </p>

        {timelineDialogs}
      </div>
    );
  }

  // Render cluster mode (normal zoom levels)
  return (
    <div className={cn("flex flex-col gap-2", className)} ref={containerRef}>
      {toolbarRow(
        <>
          {totalCountLabel}
          {timelineData.clusters.length > 0 && (
            <span className="ml-1">
              (
              {t.timeline.granularitySummary[granularity as TimelineGranularity].replace(
                "{count}",
                String(timelineData.clusters.length)
              )}
              )
            </span>
          )}
        </>
      )}

      {/* Days view: one card per document, a day's cards stacked tightly
          together on that day's side of the stem. */}
      {isDayView ? (
        <ServerTimelineDayBand
          clusters={timelineData.clusters}
          getExpandedState={getExpandedCluster}
          onLoadMore={loadMoreInCluster}
          nodeProps={documentNodeProps}
        />
      ) : (
        <div
          ref={scrollContainerRef}
          // No panel frame — see the max-zoom container above.
          className="relative overflow-visible"
          style={{ minHeight: containerHeight }}
        >
          {(() => {
            // A bucket rendering document cards occupies bucketSlotCount slots,
            // so its last card sits below its own pixelPosition. The reserved
            // count (from bucket.documentCount) can undercount the fetched docs
            // when expandCluster merges content-search matches into a window
            // whose bucket the summary merge assigned them outside of (JS vs
            // Postgres bucket-boundary edge cases) — so span each bucket by the
            // docs it will actually render, never fewer than reserved, and
            // shift everything below by the surplus so cards can't overlap.
            let slotShift = 0;
            const layouts = timelineData.clusters.map((cluster) => {
              const docs =
                cluster.showsDocumentCards && isClusterExpanded(cluster)
                  ? (getExpandedCluster(cluster)?.documents ?? [])
                  : [];
              const reserved = cluster.showsDocumentCards
                ? bucketSlotCount(cluster.documentCount)
                : 1;
              const span = cluster.showsDocumentCards
                ? Math.max(reserved, bucketSlotCount(docs.length))
                : reserved;
              const top = cluster.pixelPosition + slotShift * TIMELINE_ENTRY_GAP;
              slotShift += span - reserved;
              return { cluster, docs, top, span };
            });
            const maxPosition =
              layouts.length > 0
                ? Math.max(
                    ...layouts.map(
                      (l) =>
                        l.top +
                        (l.cluster.showsDocumentCards ? (l.span - 1) * TIMELINE_ENTRY_GAP : 0)
                    )
                  )
                : usableHeight;
            const contentHeight = Math.max(maxPosition + TIMELINE_PADDING * 2, containerHeight);

            return (
              <div
                className="relative px-6"
                style={{
                  width: "100%",
                  minHeight: contentHeight,
                  paddingTop: TIMELINE_PADDING,
                  paddingBottom: TIMELINE_PADDING,
                }}
              >
                {/* Central stem */}
                <div
                  className="pointer-events-none absolute left-1/2 -translate-x-1/2"
                  style={{
                    top: TIMELINE_PADDING / 2,
                    height: contentHeight - TIMELINE_PADDING,
                  }}
                >
                  <TimelineStem
                    height={contentHeight - TIMELINE_PADDING}
                    dateMarkers={dateMarkers}
                  />
                </div>

                {/* Render server-side clusters */}
                {layouts.map(({ cluster, docs, top }) => {
                  const adjustedPosition = top + TIMELINE_PADDING;

                  // RULE 1: Buckets that show document cards NEVER render as
                  // cluster cards — one node per document (stacked down the
                  // slots the position maths reserved), or a loading placeholder
                  // until the documents arrive. Every card of a bucket stays on
                  // the bucket's own side, so one day's documents read as one
                  // group instead of straddling the stem.
                  if (cluster.showsDocumentCards) {
                    if (docs.length > 0) {
                      return docs.map((doc, docIndex) => (
                        <ServerTimelineNode
                          key={doc.id}
                          side={cluster.side}
                          pixelPosition={adjustedPosition + docIndex * 2 * TIMELINE_ENTRY_GAP}
                          {...documentNodeProps(doc)}
                        />
                      ));
                    }
                    // Still loading or not yet expanded - render loading placeholder
                    // (auto-expand effect will trigger the fetch)
                    return (
                      <div
                        key={cluster.id}
                        className="absolute flex items-center"
                        style={{
                          top: adjustedPosition,
                          transform: "translateY(-50%)",
                          left: cluster.side === "right" ? "50%" : undefined,
                          right: cluster.side === "left" ? "50%" : undefined,
                          paddingLeft: cluster.side === "right" ? "8px" : undefined,
                          paddingRight: cluster.side === "left" ? "8px" : undefined,
                        }}
                      >
                        <div
                          className={cn(
                            "flex items-center gap-3 rounded-xl border px-4 py-3 opacity-60",
                            TIMELINE_DOCUMENT_CARD_FILL_CLASS
                          )}
                        >
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Loading...</span>
                        </div>
                      </div>
                    );
                  }

                  // RULE 2: Everything else is a cluster card, whose popover
                  // lists the bucket's documents on demand.
                  return (
                    <ServerTimelineCluster
                      key={cluster.id}
                      cluster={cluster}
                      side={cluster.side}
                      pixelPosition={adjustedPosition}
                      isInlineOpen={openClusterId === cluster.id}
                      onToggleInline={handleToggleInlineCluster}
                      onExpand={expandCluster}
                      onCollapse={collapseCluster}
                      onLoadMore={loadMoreInCluster}
                      getExpandedState={getExpandedCluster}
                      isExpanded={isClusterExpanded}
                      granularity={granularity as TimelineGranularity}
                      nodeProps={documentNodeProps}
                    />
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* Zoom hint */}
      <p className="text-center text-xs text-muted-foreground">{t.timeline.hintZoomScroll}</p>

      {timelineDialogs}
    </div>
  );
});
